import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getEquityDividendReport,
  downloadEquityDividendReportPDF,
  getFinancialYears,
  generateDefaultFinancialYears,
} from '../../services/dividendService';
import {
  formatCurrency,
  getFinancialYearMonths,
  normalizeTableData,
  calculateMonthlyTotals,
  isValidReportData,
  getEmptyStateMessage,
  downloadPDF,
} from '../../utils/equityDividendReportHelpers';
import '../../assets/css/Transaction-Section-Style/equityDividendReport.css';

/**
 * EquityDividendReport Component
 * Displays equity dividend report with financial year filter and monthly breakdown
 * 
 * Features:
 * - Financial year selection
 * - Scrip-wise data with monthly breakdown (April-March)
 * - Horizontal scrollable table for many columns
 * - Total row with footer monthly totals
 * - PDF download functionality
 * - Print functionality
 * - Error and empty state handling
 * - Loading state with spinner
 * 
 * @returns {JSX.Element} EquityDividendReport component
 */
const EquityDividendReport = () => {
  // State: Financial Year Selection
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState('');

  // State: Report Data
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Derived state: Months and normalized data
  const months = reportData?.months || getFinancialYearMonths();
  const normalizedData = normalizeTableData(reportData?.data, months);
  const monthlyTotals = calculateMonthlyTotals(normalizedData, months);
  const totalAllScrips = Object.values(monthlyTotals).reduce((sum, val) => sum + val, 0);

  /**
   * Initialize financial years on component mount
   */
  useEffect(() => {
    initializeFinancialYears();
  }, []);

  /**
   * Fetch report data when financial year changes
   */
  useEffect(() => {
    if (selectedFY) {
      fetchEquityDividendReport(selectedFY);
    }
  }, [selectedFY]);

  /**
   * Initialize financial year dropdown
   */
  const initializeFinancialYears = async () => {
    try {
      let years = [];
      try {
        years = await getFinancialYears();
      } catch {
        // Fallback to generated list
        years = generateDefaultFinancialYears();
      }

      setFinancialYears(years);

      // Set default to current financial year
      if (years.length > 0) {
        setSelectedFY(years[0]);
      }
    } catch (err) {
      console.error('Error initializing financial years:', err);
      const defaultYears = generateDefaultFinancialYears();
      setFinancialYears(defaultYears);
      if (defaultYears.length > 0) {
        setSelectedFY(defaultYears[0]);
      }
    }
  };

  /**
   * Fetch equity dividend report data from API
   */
  const fetchEquityDividendReport = useCallback(async (fy) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEquityDividendReport(fy);
      
      // Validate data structure
      if (isValidReportData(data)) {
        setReportData(data);
      } else {
        throw new Error('Invalid data structure from API');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch equity dividend report';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Error fetching equity dividend report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Handle PDF download
   */
  const handleDownloadPDF = useCallback(async () => {
    if (!selectedFY) {
      toast.error('Please select a financial year');
      return;
    }

    try {
      setLoading(true);
      const pdfBlob = await downloadEquityDividendReportPDF(selectedFY);
      downloadPDF(pdfBlob, selectedFY, 'Equity_Dividend_Report');
      toast.success('PDF downloaded successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to download PDF';
      toast.error(errorMsg);
      console.error('Error downloading PDF:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFY]);

  /**
   * Handle print functionality
   * Opens PDF in new tab and triggers print
   */
  const handlePrint = useCallback(async () => {
    if (!selectedFY) {
      toast.error('Please select a financial year');
      return;
    }

    try {
      setLoading(true);
      const pdfBlob = await downloadEquityDividendReportPDF(selectedFY);
      const url = window.URL.createObjectURL(pdfBlob);
      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback: Use window.print() if new tab blocked
        toast.info('Print window blocked. Using browser print instead.');
        window.print();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to print report';
      toast.error(errorMsg);
      console.error('Error printing report:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFY]);

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    window.history.back();
  };

  /**
   * Handle financial year change
   */
  const handleYearChange = (e) => {
    const year = e.target.value;
    if (year) {
      setSelectedFY(year);
    }
  };

  /**
   * Render Empty State
   */
  const renderEmptyState = () => {
    const message = getEmptyStateMessage(!!error, !reportData);
    return (
      <div className="equity-dividend-table-container">
        <div className="equity-dividend-empty-state">
          <h3>{message.title}</h3>
          <p>{message.message}</p>
        </div>
      </div>
    );
  };

  /**
   * Render Loading State
   */
  const renderLoading = () => (
    <div className="equity-dividend-table-container">
      <div className="equity-dividend-loading">
        <div className="equity-dividend-spinner" />
      </div>
    </div>
  );

  /**
   * Render Error Banner
   */
  const renderErrorBanner = () => {
    if (!error) return null;

    return (
      <div className="equity-dividend-error">
        <div className="equity-dividend-error-message">
          <strong>Error:</strong> {error}
        </div>
        <button onClick={() => setError(null)}>Dismiss</button>
      </div>
    );
  };

  /**
   * Render Table Header
   */
  const renderTableHeader = () => (
    <thead>
      <tr>
        <th className="scrip-name-header">Scrip Name</th>
        {months.map((month) => (
          <th key={month}>{month}</th>
        ))}
        <th className="total-header">Total</th>
      </tr>
    </thead>
  );

  /**
   * Render Table Body with data rows
   */
  const renderTableBody = () => (
    <tbody>
      {/* Data Rows */}
      {normalizedData.map((row, index) => (
        <tr key={`scrip-${index}`}>
          <td className="scrip-name-cell">{row.scrip}</td>
          {months.map((month) => (
            <td key={`${row.scrip}-${month}`} className="number-cell">
              {formatCurrency(row.monthly[month])}
            </td>
          ))}
          <td className="total-cell number-cell">
            {formatCurrency(row.total)}
          </td>
        </tr>
      ))}

      {/* Total Row */}
      <tr className="total-row">
        <td className="scrip-name-cell">Total</td>
        {months.map((month) => (
          <td key={`total-${month}`} className="number-cell">
            {formatCurrency(monthlyTotals[month])}
          </td>
        ))}
        <td className="total-cell number-cell">
          {formatCurrency(totalAllScrips)}
        </td>
      </tr>
    </tbody>
  );

  /**
   * Render Main Table
   */
  const renderTable = () => {
    if (loading) return renderLoading();
    if (!reportData || normalizedData.length === 0) return renderEmptyState();

    return (
      <div className="equity-dividend-table-container">
        <div className="equity-dividend-table-wrapper">
          <table className="equity-dividend-table">
            {renderTableHeader()}
            {renderTableBody()}
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="equity-dividend-wrapper">
      {/* Top Controls */}
      <div className="equity-dividend-top-controls">
        <div className="equity-dividend-left-controls">
          <a
            href="#"
            className="equity-dividend-back-button"
            onClick={(e) => {
              e.preventDefault();
              handleBack();
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </a>
          <button
            className="equity-dividend-print-button"
            onClick={handlePrint}
            disabled={loading || !reportData}
            title="Print the report"
          >
            Print
          </button>
          <button
            className="equity-dividend-download-button"
            onClick={handleDownloadPDF}
            disabled={loading || !reportData}
            title="Download as PDF"
          >
            Download
          </button>
        </div>

        <div className="equity-dividend-right-controls">
          <label htmlFor="equity-dividend-year-select">Financial Year:</label>
          <select
            id="equity-dividend-year-select"
            className="equity-dividend-year-select"
            value={selectedFY}
            onChange={handleYearChange}
            disabled={loading || financialYears.length === 0}
          >
            <option value="">Select Financial Year</option>
            {financialYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Container */}
      <div className="equity-dividend-main-container">
        <h1 className="equity-dividend-main-title">Equity Dividend Report</h1>
        <hr className="equity-dividend-divider" />

        {/* Error Banner */}
        {renderErrorBanner()}

        {/* Table */}
        {renderTable()}
      </div>
    </div>
  );
};

export default EquityDividendReport;
